/* eslint-disable no-mixed-operators */

/*
Capabilities:
- CAP-PROMOTION-PARTICIPATION

Responsibilities:
- Render the code-based offer-claim promotion modal and map backend claim
  results into member-facing UI states.

Context:
- project-intelligence/capabilities/requested/member/promotion-participation.md
*/

import React, { useState, useRef } from 'react';

import Form from 'react-bootstrap/Form';
import Button from 'react-bootstrap/Button';
import Modal from 'react-bootstrap/Modal';
import CloseButton from 'react-bootstrap/CloseButton';

import RESTUtils from '../../../utils/rest-utils';

import { HorizontallyCenteringContainer, VerticalSeparator } from '../utils';

const PROMO_IMAGE = 'redeem-offer-img-ssw-q1-2024-promo.png';
const REDEEM_OFFER_IMAGE_HEADER_FILENAME = 'redeem-offer-img-ssw-q1-2024-header-v2.jpg';
const REDEEM_OFFER_IMAGE_FOOTER_FILENAME = 'redeem-offer-img-ssw-q1-2024-footer-v2.jpg';

const CLOSE_BUTTON_VARIANT = null;  // 'white';

export default function RedeemOfferWithCode({
    showModal, setShowModal,
    token, memberData, updateMemberData
}) {
    /*
    CAP-PROMOTION-PARTICIPATION entry point for code-based offer claim.

    This modal posts the current member token plus a campaign/code tuple, then
    refreshes member data on success so newly granted personal vouchers become
    visible immediately.
    */
    const [redeemOfferResult, setRedeemOfferResult] = useState(null);
    const redeemOfferCodeInputRef = useRef(null);

    const doRedeem = e => {
        if (redeemOfferCodeInputRef?.current?.value) {
            const code = redeemOfferCodeInputRef.current.value.toString().trim()
            if (!!code) {
                setRedeemOfferResult('Redeeming...')

                e.target.setAttribute('disabled', true);
                redeemOfferCodeInputRef.current.setAttribute('disabled', true);

                const enableElements = () => {
                    e.target.removeAttribute('disabled');
                    redeemOfferCodeInputRef.current.removeAttribute('disabled');
                }

                RESTUtils.call('/promotions/claim-redemption-offer', {
                    token,
                    campaign: 'scratch_and_sure_win_2024_02',
                    campaign_type: 'personal-voucher',
                    code: code,
                }, { method: 'POST' })
                    .then(r => {
                        if (r?.data?.success) {
                            setRedeemOfferResult('Success. Please wait...')
                            if (!!updateMemberData) {
                                updateMemberData()
                                    .then(() => {
                                        setShowModal(false)
                                        setRedeemOfferResult(null)
                                    })
                            }
                        } else {
                            const failureReason = r?.data?.reason;
                            if (failureReason === 'not-available') {
                                setTimeout(() => {
                                    // Wait an extra 3 seconds
                                    setRedeemOfferResult(`Code Not Valid`);
                                    enableElements();
                                }, 3000);
                            } else if (failureReason === 'previously-claimed') {
                                setRedeemOfferResult(`Offer Previously Redeemed`);
                                enableElements();
                            } else if (failureReason === 'fully-redeemed') {
                                setRedeemOfferResult(`Offer Fully Redeemed`);
                                enableElements();
                            } else {
                                setRedeemOfferResult(`Redemption Failed`);
                                enableElements();
                            }
                        }
                    })
                    .catch(() => {
                        setRedeemOfferResult(`Redemption Failed (Try Again Later)`);
                        enableElements();
                    })
            }
        }
    };

    const redeemTextColor = redeemOfferResult?.toString().toLowerCase().startsWith('success') ? 'green' : (
        redeemOfferResult?.toString().toLowerCase().startsWith('redeeming') ? 'black' : 'red'
    );

    return (
        <Modal
            show={showModal} fullscreen={true}
        >
            <Modal.Body>
                <div style={{
                    position:'absolute', zIndex: '1',
                    top: '1em', left: '0px', 
                    width: 'calc(100% - 1em)',
                    height: '3em',
                    textAlign: 'right',
                }}>
                    <CloseButton
                        variant={CLOSE_BUTTON_VARIANT}
                        onClick={() => setShowModal(false)}
                    />
                </div>
                {
                    REDEEM_OFFER_IMAGE_HEADER_FILENAME && (
                        <div style={{
                            width: 'calc(100% + calc(2*var(--bs-modal-padding))',
                            margin: 'calc(-1*var(--bs-modal-padding))',
                        }}>
                            <img
                                src={REDEEM_OFFER_IMAGE_HEADER_FILENAME} alt="Redeem Offer"
                                style={{
                                    width: '100%'
                                }}
                            />
                        </div>
                    )
                }
                <VerticalSeparator height='calc(2*var(--bs-modal-padding))'/>
                
                <HorizontallyCenteringContainer>
                    <div style={{ width: '40%', zIndex: '2' }}>
                        <div>
                            <Form.Control
                                ref={redeemOfferCodeInputRef} type="text" placeholder="8888"
                                maxLength={4}
                                style={{ textAlign: 'center', fontSize: '3em' }}
                            />
                        </div>
                    </div>
                </HorizontallyCenteringContainer>
                <VerticalSeparator height='1em'/>
                <HorizontallyCenteringContainer>
                    <div style={{ width: '80%', zIndex: '2' }}>
                        <Button className="w100 swensens-button"
                            onClick={doRedeem}
                        >Redeem</Button>
                    </div>
                </HorizontallyCenteringContainer>
                <HorizontallyCenteringContainer>
                    <div style={{ color: redeemTextColor }}>
                        {redeemOfferResult}
                    </div>
                </HorizontallyCenteringContainer>
                <VerticalSeparator height='1em'/>

                {
                    REDEEM_OFFER_IMAGE_FOOTER_FILENAME && (
                        <div style={{
                            width: 'calc(100% + calc(2*var(--bs-modal-padding))',
                            marginLeft: 'calc(-1*var(--bs-modal-padding))',
                        }}>
                            <img
                                src={REDEEM_OFFER_IMAGE_FOOTER_FILENAME} alt="Offer Info"
                                style={{
                                    width: '100%'
                                }}
                            />
                        </div>
                    )
                }

                <VerticalSeparator height='3em'/>
            </Modal.Body>
        </Modal>
    )
}

RedeemOfferWithCode.__heading__ = 'Lunar New Year 2024|Scratch and Sure Win';
RedeemOfferWithCode.__imgSrc__ = PROMO_IMAGE;
// RedeemOfferWithCode.__display_start_date__ = isDev ? new Date('2024-01-01') : new Date('2024-02-19');
RedeemOfferWithCode.__start_date__ = new Date('2024-01-01');
RedeemOfferWithCode.__end_date__ = new Date('2024-04-07');
// RedeemOfferWithCode.__override_condition__ = () => isDev;
// RedeemOfferWithCode.__additional_condition__ = () => true;

RedeemOfferWithCode.__additional_styles__ = {
    border: '4px solid gold',
    marginLeft: '-4px',
    marginRight: '-4px',
    // border: '4px solid var(--swensens-brand-color)',
    // backgroundColor: 'goldenrod',
};
